import { CURSOR_BEHAVIOR } from './cursorBehavior';
import {
  BAD_SIGNAL_NETWORK_ENABLED,
  BAD_SIGNAL_REQUIRES_DISTRICT_THRESHOLD,
  BAD_SIGNAL_WCPM_PIVOT,
  HIGH_PRECISION_BRANCHES,
} from './signalThresholds';

/**
 * TT-991's bad-signal cursor decision table. Pure — every input is an argument, which makes the
 * full permutation table testable without a session; CursorOrchestrator owns the consequences.
 * Neither outcome has an automatic exit (TT-991 AC): the student stays in the mode on every later
 * launch until a CSM-team member nulls SIS metadata.badSignalAltCursorMode (GT-326). That is why
 * cursor-free is gated on TT-1230's high-precision branches while tap-to-read, being reversible,
 * takes the full recall-tuned rule. Bad network (GT-306: TT-1377's chronic high-RTT rule over the login history)
 * takes the same two outcomes once BAD_SIGNAL_NETWORK_ENABLED is on.
 */

// Returns null for "no opinion — fall through", NEVER "force standard": every failed gate and every
// unknown answers null. Inputs are read strictly, so coercible junk answers null rather than opting in.
export function resolveBadSignalBehavior({
  hadBadAudio = null,
  hadBadNetwork = null,
  audioBranches = null,
  wcpm = null,
  isIpad = false,
  tapDisabled = false,
  cursorFreeDisabled = false,
  cursorFreeConfigured = false,
  wcpmPivot = BAD_SIGNAL_WCPM_PIVOT,
  audioSignalOff = false,
  networkSignalOff = false,
} = {}) {
  // 1. Unknown is not bad. Only an exact true is. Network sits behind its switch, so while that is
  // off its verdict arrives and is ignored here rather than never being computed. This has to stay
  // INSIDE gate 1: an early return on the switch would disable the audio path too, i.e. the whole
  // branch. The same holds for GT-306's per-signal license kill switches: each silences only its
  // own signal, so the other can still decide alone.
  const audioIsBad = audioSignalOff !== true && hadBadAudio === true;
  const networkIsBad = BAD_SIGNAL_NETWORK_ENABLED && networkSignalOff !== true && hadBadNetwork === true;
  if (!audioIsBad && !networkIsBad) {
    return null;
  }

  // 2. Cannot place the student against the pivot.
  if (!Number.isFinite(wcpm)) {
    return null;
  }

  const pivot = Number.isFinite(wcpmPivot) ? wcpmPivot : BAD_SIGNAL_WCPM_PIVOT;

  // 3. At or below the pivot (the pivot itself falls this side): tap-to-read, iPad only. Reversible,
  // so any branch of the full recall-tuned rule is enough — the check in gate 1 was it.
  if (wcpm <= pivot) {
    if (isIpad === true && tapDisabled !== true) {
      return CURSOR_BEHAVIOR.TAP_TO_READ;
    }
    return null; // ticket: "If no iPad: No behavior change"
  }

  // 4. Above the pivot: cursor-free, and only strong evidence earns it — TT-1230's two
  // high-precision audio branches, or (GT-306) TT-1377's chronic high-RTT rule. The network rule's
  // justification is a different metric from the audio branches' precision: it flags 1.1% of
  // matched controls (a false-positive rate) and 19.7% of the studied population, and product
  // accepted network-only cursor-free on that basis (GT-306). The old badCount placeholder never
  // reached this side.
  const highPrecisionAudio = audioIsBad
    && HIGH_PRECISION_BRANCHES.some((branch) => audioBranches?.[branch] === true);
  if (!highPrecisionAudio && !networkIsBad) {
    return null;
  }

  // 5. ...and not if cursor-free has been switched off. forceCursorFreeOff always vetoes, though it
  // is platform-wide rather than per-district in this client. Never having configured
  // cursorFreeTutorWCPMThreshold no longer vetoes: waived so that a detected bad signal resolves on
  // the WCPM pivot alone. The gate stays behind the switch rather than being deleted, so
  // re-requiring it is a value change.
  if (cursorFreeDisabled === true) {
    return null;
  }
  if (BAD_SIGNAL_REQUIRES_DISTRICT_THRESHOLD && cursorFreeConfigured !== true) {
    return null;
  }
  return CURSOR_BEHAVIOR.CURSOR_FREE;
}
